import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pwh201Api } from '../../../services/pwh201Api';
import type { FH110, FH111, FH113 } from '../../../services/pwh201Api';

export function usePWH201Record() {
  const [record, setRecord] = useState<FH110 | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const data = await pwh201Api.get(id);
      setRecord(data);
      return data;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '載入失敗';
      message.error('載入失敗：' + msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const save = useCallback(async (
    mode: 'add' | 'edit',
    master: Partial<FH110>,
    expenseDetails: Partial<FH111>[],
    invoiceDetails: Partial<FH113>[],
  ) => {
    setSaving(true);
    try {
      const payload = { master, expense_details: expenseDetails, invoice_details: invoiceDetails };
      let result: FH110;
      if (mode === 'add') {
        result = await pwh201Api.create(payload);
      } else {
        result = await pwh201Api.update(master.fh110_01!, payload);
      }
      setRecord(result);
      message.success('存檔成功');
      return result;
    } catch (err: unknown) {
      const apiErr = (err as { response?: { data?: { errors?: Array<{ message: string }>; error?: string } } })?.response?.data;
      if (apiErr?.errors) {
        const msgs = apiErr.errors.map(e => e.message).join('；');
        message.error('驗證錯誤：' + msgs);
      } else {
        const msg = apiErr?.error || (err instanceof Error ? err.message : '存檔失敗');
        message.error('存檔失敗：' + msg);
      }
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  const remove = useCallback(async (id: string) => {
    setSaving(true);
    try {
      await pwh201Api.delete(id);
      setRecord(null);
      message.success('刪除成功');
      return true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '刪除失敗';
      message.error('刪除失敗：' + msg);
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  return { record, loading, saving, load, save, remove, setRecord };
}
